import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './BrowseInitiatives.css';
import '../styles/ResizableTable.css';
import { initiativeService, Initiative } from '../services/api';
import { api } from '../config';
import Spinner from './Spinner';
import MultiSelectDropdown from './MultiSelectDropdown';
import { initResizableTables } from '../utils/resizableTables';
import { getDivisions } from '../utils/departments';

const BrowseInitiatives: React.FC = () => {
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInitiative, setSelectedInitiative] = useState<Initiative | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(10);
  const [engagementSummary, setEngagementSummary] = useState<Record<string, { like_count: number; partner_count: number }>>({});
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [divisionFilter, setDivisionFilter] = useState<string[]>([]);
  const [ownerFilter, setOwnerFilter] = useState<string[]>([]);
  
  // Initialize submitted months to current month
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };
  
  const [submittedMonths, setSubmittedMonths] = useState<string[]>([getCurrentMonth()]);
  type SortColumn = 'use_case_name' | 'purpose' | 'proposed_solution_owner' | 'submitted_by' | 'submitted_date' | 'status' | 'engagement';
  const [sortColumn, setSortColumn] = useState<SortColumn>('submitted_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const itemsPerScroll = 10;
  const scrollWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const fetchInitiatives = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await initiativeService.getInitiatives();
        // Sort by submitted_date (newest first)
        const allInitiatives = response.data
          .sort((a: Initiative, b: Initiative) => new Date(b.submitted_date).getTime() - new Date(a.submitted_date).getTime());
        setInitiatives(allInitiatives);
        if (allInitiatives.length > 0) {
          try {
            const ids = allInitiatives.map((i: Initiative) => i.id).join(',');
            const res = await axios.get(api(`/api/initiatives/engagements/summary?initiative_ids=${ids}`));
            setEngagementSummary(res.data);
          } catch { /* silent */ }
        }
      } catch (err: any) {
        setError('Failed to load initiatives');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitiatives();
  }, []);

  // Initialize resizable tables when initiatives load
  useEffect(() => {
    if (!isLoading && initiatives.length > 0) {
      const timer = setTimeout(() => {
        initResizableTables();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isLoading, initiatives.length]);

  // Search and filter
  const filteredInitiatives = initiatives.filter((initiative) => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = (
      (initiative.use_case_name?.toLowerCase().includes(searchLower) || '') ||
      (initiative.purpose?.toLowerCase().includes(searchLower) || '') ||
      (initiative.proposed_solution_owner?.toLowerCase().includes(searchLower) || '')
    );
    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(initiative.status || 'pending');
    const matchesDivision = divisionFilter.length === 0 || divisionFilter.includes(initiative.division || 'Unassigned');
    const matchesOwner = ownerFilter.length === 0 || ownerFilter.includes(initiative.proposed_solution_owner || 'N/A');
    
    let matchesMonth = true;
    if (submittedMonths.length > 0) {
      const submittedDate = new Date(initiative.submitted_date);
      const initiativeYearMonth = `${submittedDate.getFullYear()}-${String(submittedDate.getMonth() + 1).padStart(2, '0')}`;
      matchesMonth = submittedMonths.includes(initiativeYearMonth);
    }
    
    return matchesSearch && matchesStatus && matchesDivision && matchesOwner && matchesMonth;
  });

  // Sort
  const sortedInitiatives = [...filteredInitiatives].sort((a, b) => {
    let result = 0;
    if (sortColumn === 'submitted_date') {
      result = new Date(a.submitted_date).getTime() - new Date(b.submitted_date).getTime();
    } else if (sortColumn === 'engagement') {
      const engA = engagementSummary[String(a.id)];
      const engB = engagementSummary[String(b.id)];
      const scoreA = (engA?.like_count || 0) + (engA?.partner_count || 0);
      const scoreB = (engB?.like_count || 0) + (engB?.partner_count || 0);
      result = scoreA - scoreB;
    } else {
      const valA = (a[sortColumn] || '').toString().toLowerCase();
      const valB = (b[sortColumn] || '').toString().toLowerCase();
      result = valA.localeCompare(valB);
    }
    return sortDirection === 'asc' ? result : -result;
  });

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setVisibleCount(10);
  };

  const sortIndicator = (column: SortColumn) => {
    if (sortColumn !== column) return <span className="sort-icon">⇅</span>;
    return <span className="sort-icon active">{sortDirection === 'asc' ? '▲' : '▼'}</span>;
  };

  // Pagination
  const displayedInitiatives = sortedInitiatives.slice(0, visibleCount);

  // Reset visible count when the filtered list changes (e.g. new search)
  useEffect(() => {
    setVisibleCount(10);
  }, [searchQuery, statusFilter, divisionFilter, ownerFilter, submittedMonths]);

  const clearAdvancedFilters = () => {
    setStatusFilter([]);
    setDivisionFilter([]);
    setOwnerFilter([]);
    setSubmittedMonths([getCurrentMonth()]);
  };

  const advancedFiltersActive = statusFilter.length > 0 || divisionFilter.length > 0 || ownerFilter.length > 0 || submittedMonths.length > 0;

  const handleScroll = useCallback(() => {
    const el = scrollWrapperRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      setVisibleCount((prev) => Math.min(sortedInitiatives.length, prev + itemsPerScroll));
    }
  }, [sortedInitiatives.length]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatMonthDisplay = (yearMonth: string) => {
    const [year, month] = yearMonth.split('-');
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const parseMonthDisplay = (displayMonth: string) => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    const parts = displayMonth.split(' ');
    const year = parts[1];
    const month = (monthNames.indexOf(parts[0]) + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  };

  if (isLoading) {
    return (
      <div className="browse-initiatives-container">
        <Spinner message="Loading approved initiatives..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="browse-initiatives-container">
        <div style={{ textAlign: 'center', padding: '40px', color: '#d32f2f' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="browse-initiatives-container">
      <div className="browse-initiatives-header">
        <p className="initiatives-count">Showing all initiatives ({initiatives.length})</p>
      </div>

      {initiatives.length === 0 ? (
        <div className="no-initiatives-message">
          <p>No approved initiatives yet.</p>
        </div>
      ) : (
        <>
          <div className="browse-search-section">
            <input
              type="text"
              placeholder="Search by title, description, or division..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              className="search-input"
            />
            <button
              type="button"
              className={`advanced-filters-toggle${advancedFiltersActive ? ' active' : ''}`}
              onClick={() => setShowAdvancedFilters((prev) => !prev)}
            >
              Advanced Filters{advancedFiltersActive ? ' •' : ''} {showAdvancedFilters ? '▲' : '▼'}
            </button>
          </div>

          {showAdvancedFilters && (
            <div className="advanced-filters-panel">
              <MultiSelectDropdown
                label="Status"
                options={['Pending', 'Approved', 'Rejected', 'Parked']}
                selected={statusFilter.map(s => s.charAt(0).toUpperCase() + s.slice(1))}
                onChange={(selected) => setStatusFilter(selected.map(s => s.toLowerCase()))}
                placeholder="All statuses"
              />
              <MultiSelectDropdown
                label="Division"
                options={[...getDivisions(), 'Unassigned']}
                selected={divisionFilter}
                onChange={setDivisionFilter}
                placeholder="All divisions"
              />
              <MultiSelectDropdown
                label="Owner"
                options={Array.from(new Set(initiatives.map(i => i.proposed_solution_owner || 'N/A'))).sort()}
                selected={ownerFilter}
                onChange={setOwnerFilter}
                placeholder="All owners"
              />
              <MultiSelectDropdown
                label="Submitted Month"
                options={Array.from(new Set(initiatives.map(i => {
                  const date = new Date(i.submitted_date);
                  const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                  return formatMonthDisplay(yearMonth);
                }))).sort((a, b) => {
                  const aYearMonth = parseMonthDisplay(a);
                  const bYearMonth = parseMonthDisplay(b);
                  return bYearMonth.localeCompare(aYearMonth);
                })}
                selected={submittedMonths.map(formatMonthDisplay)}
                onChange={(selected) => setSubmittedMonths(selected.map(parseMonthDisplay))}
                placeholder="All months"
              />
              <button type="button" className="advanced-filters-clear" onClick={clearAdvancedFilters} disabled={!advancedFiltersActive}>
                Clear
              </button>
            </div>
          )}

          {displayedInitiatives.length === 0 ? (
            <div className="no-results-message">No initiatives match your search.</div>
          ) : (
            <>
              <div className="browse-table-wrapper browse-table-scroll" ref={scrollWrapperRef} onScroll={handleScroll}>
                <table id="browse-initiatives-table" className="initiatives-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Division</th>
                      <th className="sortable-th" onClick={() => handleSort('use_case_name')}>Use Case {sortIndicator('use_case_name')}</th>
                      <th className="sortable-th" onClick={() => handleSort('purpose')}>Purpose {sortIndicator('purpose')}</th>
                      <th className="col-owner sortable-th" onClick={() => handleSort('proposed_solution_owner')}>Owner {sortIndicator('proposed_solution_owner')}</th>
                      <th className="sortable-th" onClick={() => handleSort('submitted_by')}>Submitted By {sortIndicator('submitted_by')}</th>
                      <th className="sortable-th" onClick={() => handleSort('submitted_date')}>Date {sortIndicator('submitted_date')}</th>
                      <th style={{textAlign:'center'}} className="sortable-th" onClick={() => handleSort('status')}>Status {sortIndicator('status')}</th>
                      <th style={{textAlign:'center'}}>Action</th>
                      <th style={{textAlign:'center'}} className="sortable-th" onClick={() => handleSort('engagement')}>Engagement {sortIndicator('engagement')}</th>
                      <th style={{width:'24px', minWidth:'24px', maxWidth:'24px', padding:0}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedInitiatives.map((initiative, idx) => (
                      <tr key={initiative.id}>
                        <td>{idx + 1}</td>
                        <td>{initiative.division || 'Unassigned'}</td>
                        <td className="col-usecase">{initiative.use_case_name}</td>
                        <td className="col-purpose">{initiative.purpose?.substring(0, 100)}{initiative.purpose && initiative.purpose.length > 100 ? '...' : ''}</td>
                        <td className="col-owner">{initiative.proposed_solution_owner || 'N/A'}</td>
                        <td>{initiative.submitted_by || 'N/A'}</td>
                        <td className="col-date">{formatDate(initiative.submitted_date || '')}</td>
                        <td style={{textAlign:'center'}}>
                          <span className={`status-badge ${(initiative.status || 'pending').toLowerCase()}`}>
                            {initiative.status === 'approved' ? 'Approved' : initiative.status === 'rejected' ? 'Rejected' : initiative.status === 'parked' ? 'Parked' : 'Pending'}
                          </span>
                        </td>
                        <td style={{textAlign:'center'}}><button className="view-details-btn" onClick={() => setSelectedInitiative(initiative)}>Details</button></td>
                        <td style={{textAlign:'center', fontSize:12}}>
                          {(() => {
                            const eng = engagementSummary[String(initiative.id)];
                            if (!eng || (eng.like_count === 0 && eng.partner_count === 0)) return <span style={{color:'#adb5bd'}}>—</span>;
                            return (
                              <span style={{display:'flex',flexDirection:'column',gap:2,alignItems:'center'}}>
                                {eng.like_count > 0 && <span style={{color:'#6941c6'}}>👍 {eng.like_count}</span>}
                                {eng.partner_count > 0 && <span style={{color:'#17b26a'}}>🤝 {eng.partner_count}</span>}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{width:'24px', minWidth:'24px', maxWidth:'24px', padding:0}}></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {visibleCount < sortedInitiatives.length && (
                  <div className="browse-scroll-loading">Loading more...</div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {selectedInitiative && (
        <div className="modal-overlay" onClick={() => setSelectedInitiative(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedInitiative.use_case_name}</h3>
              <button
                className="close-btn"
                onClick={() => setSelectedInitiative(null)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <strong>Status:</strong>
                <span className="status-badge approved">Approved</span>
              </div>
              <div className="detail-row">
                <strong>Solution Owner:</strong>
                <span>{selectedInitiative.proposed_solution_owner || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <strong>Sponsor:</strong>
                <span>{selectedInitiative.solution_sponsor || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <strong>Submitted by:</strong>
                <span>{selectedInitiative.submitted_by || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <strong>Submitted on:</strong>
                <span>{formatDate(selectedInitiative.submitted_date || '')}</span>
              </div>
              {selectedInitiative.approval_date && (
                <div className="detail-row">
                  <strong>Approved on:</strong>
                  <span>{formatDate(selectedInitiative.approval_date)}</span>
                </div>
              )}
              <div className="detail-section">
                <strong>Purpose:</strong>
                <p>{selectedInitiative.purpose}</p>
              </div>
              {selectedInitiative.proposed_workflow && (
                <div className="detail-section">
                  <strong>Proposed Workflow:</strong>
                  <p>{selectedInitiative.proposed_workflow}</p>
                </div>
              )}
              {selectedInitiative.value && (
                <div className="detail-section">
                  <strong>Business Value:</strong>
                  <p>{selectedInitiative.value}</p>
                </div>
              )}
              {selectedInitiative.solution_scope && (
                <div className="detail-section">
                  <strong>Solution Scope:</strong>
                  <p>{selectedInitiative.solution_scope}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrowseInitiatives;
